import { useEffect } from "react"
import { t } from "../../i18n"

export default function CwConfirmDialog({
  isOpen,
  title,
  message,
  error,
  confirmLabel,
  submitting = false,
  onConfirm,
  onClose,
}: {
  isOpen: boolean
  title: string
  message: string
  error?: string
  confirmLabel?: string
  submitting?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, onClose, submitting])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label={t("common.close")}
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          if (!submitting) onClose()
        }}
        disabled={submitting}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cw-confirm-title"
          className="relative w-full max-w-md rounded-2xl bg-white shadow-xl p-6 md:p-8 font-source"
        >
          <h2 id="cw-confirm-title" className="font-oswald text-3xl font-bold text-primary-dark">
            {title}
          </h2>
          <p className="mt-3 text-gray-700">{message}</p>
          {error ? (
            <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>
          ) : null}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-full border border-gray-300 bg-white px-5 py-2 font-semibold text-primary-dark disabled:opacity-60"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-60"
            >
              {submitting ? t("common.deleting") : (confirmLabel || t("common.delete"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
